import React from 'react';
import type { SVGProps } from 'react';

import Button from '@/components/shared/ui/buttons/Button';
import type { ButtonProps } from '@/components/shared/ui/buttons/Button';

type ButtonColor = NonNullable<ButtonProps['color']>;

/**
 * ### CtaSurfaceTone
 *
 * Background tone of the surface a call-to-action button sits on. `dark` is
 * the near-black surface the solution pages and the home claim section paint;
 * `light` is the white surface. A caller names the surface rather than a color
 * so every call to action on the same tone comes out identical.
 */
export type CtaSurfaceTone = 'dark' | 'light';

/**
 * ### CTA_BUTTON_COLOR_BY_TONE
 *
 * Resolves a surface tone to the Button `color` a call to action uses on it.
 * The gray fill is the only button that keeps contrast on the dark surface;
 * on the light surface the brand primary is the site-wide call to action.
 */
export const CTA_BUTTON_COLOR_BY_TONE: Record<CtaSurfaceTone, ButtonColor> = {
  dark: 'gray',
  light: 'primary',
};

/**
 * The outlined button is white-filled by design, which reads louder than the
 * filled action next to it once the surface is near-black. On the dark surface
 * the outline is drawn on the background instead, so the filled action stays
 * the one the eye lands on first.
 */
const DARK_LINE_CLASS =
  'border border-[rgba(255,255,255,0.4)] bg-transparent text-apps-coreGrayWhite ' +
  'hover:border-apps-coreGrayWhite hover:bg-[rgba(255,255,255,0.12)]';

/**
 * ### resolveToneClass
 *
 * Utility classes the tone adds on top of the Button color, or undefined when
 * the color alone already suits the surface. Utilities outrank the component
 * layer the base button styles live in, so what is returned here wins.
 *
 * @param tone Surface the button is placed on
 * @param variant Button emphasis
 * @returns The extra classes, or undefined when the tone adds none
 */
function resolveToneClass(
  tone: CtaSurfaceTone,
  variant: NonNullable<ButtonProps['variant']>,
): string | undefined {
  return tone === 'dark' && variant === 'line' ? DARK_LINE_CLASS : undefined;
}

/**
 * ### CtaActionButtonProps
 *
 * Destination, label, emphasis, and surface tone of one call to action.
 * Color, size, and the leading arrow are fixed by this component, so they are
 * removed from what a caller may pass; everything else Button accepts —
 * including the `data-analytics-*` attributes the landing analytics hook reads
 * off the clicked element — is forwarded untouched.
 */
export type CtaActionButtonProps = Omit<
  ButtonProps,
  'color' | 'size' | 'shape' | 'iconLeft' | 'iconRight'
> & {
  /**
   * ### tone
   *
   * Surface the button is placed on, which decides its color.
   *
   * @defaultValue `'dark'` — the near-black surface
   */
  tone?: CtaSurfaceTone;
};

/**
 * ### CtaActionButton
 *
 * The site's call-to-action button: a large link button led by the outward
 * arrow, filled for the one primary action and outlined for the rest, in the
 * emphasis the surface tone calls for. Every closing call to action renders
 * through this component rather than styling a Button itself, so a CTA reads
 * the same on the home page and on the solution pages.
 *
 * @param props Destination, label, emphasis, surface tone, and any attributes forwarded to Button
 * @returns The rendered call-to-action button element
 * @see {@link CtaActionButtonProps} Defaults applied to omitted props
 */
export default function CtaActionButton({
  tone = 'dark',
  variant = 'fill',
  className,
  children,
  ...buttonProps
}: CtaActionButtonProps) {
  const toneClass = resolveToneClass(tone, variant);

  return (
    <Button
      {...buttonProps}
      variant={variant}
      color={CTA_BUTTON_COLOR_BY_TONE[tone]}
      size="lg"
      iconLeft={<ArrowUpRightIcon aria-hidden="true" />}
      className={[toneClass, className].filter(Boolean).join(' ') || undefined}
    >
      {children}
    </Button>
  );
}

/**
 * ### ArrowUpRightIcon
 *
 * Outward-arrow glyph placed before every call-to-action label.
 *
 * @param props Presentation attributes forwarded to the svg element
 * @returns The rendered icon element
 */
function ArrowUpRightIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      {...props}
    >
      <path d="M7 7h10v10" />
      <path d="M7 17 17 7" />
    </svg>
  );
}
